import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfoMessage from "hooks/useInfoMessage";
import useAddOneRepMaxDialog from "../providers/AddOneRepMaxDialogProvider/useAddOneRepMaxDialog";
import { OneRepMaxEntriesTableRoot, OneRepMaxEntryAuthor, OneRepMaxEntryValue } from "./components";
import VirtualizedDataTable from "components/VirtualizedDataTable";
import type { DataTableColumn } from "components/VirtualizedDataTable";
import type { Variants } from "types";
import DeleteOneRepMaxDialog from "./components/DeleteOneRepMaxDialog";
import OneRepMaxEntriesTableHeader from "./components/OneRepMaxEntriesTableHeader";
import OneRepMaxEntryDeleteButton from "./components/OneRepMaxEntryDeleteButton";
import useOneRepMaxEntriesTableRows from "./hooks/useOneRepMaxEntriesTableRows";
import type { OneRepMaxEntriesTableRow } from "./hooks/useOneRepMaxEntriesTableRows";
import useOneRepMaxDeleteMutation from "hooks/useOneRepMaxDeleteMutation";
import useChartSelections from "components/charting/hooks/useChartSelections";

const ESTIMATE_ROW_HEIGHT = 48;

const estimateSize = () => ESTIMATE_ROW_HEIGHT;
const keyExtractor = (row: OneRepMaxEntriesTableRow) => row.id;

function OneRepMaxEntriesTable() {
  const { t } = useTranslation();
  const rows = useOneRepMaxEntriesTableRows();

  const { sources } = useChartSelections();
  const athleteId = sources[0]?.id ?? "";
  const { openAddOneRepMaxDialog, disabled: addDisabled, addButtonRef } = useAddOneRepMaxDialog();

  const errorMessage = useInfoMessage({ type: "error" });
  const successMessage = useInfoMessage({ type: "success" });
  const { trigger: deleteOneRepMax, status: deleteStatus } = useOneRepMaxDeleteMutation(athleteId);
  // Kept after closing so the dialog doesn't lose its text while fading out.
  const [selectedRow, setSelectedRow] = useState<OneRepMaxEntriesTableRow | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const deleteButtonsRef = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterDeleteRef = useRef<{ neighborId: string | null } | null>(null);

  const openDeleteDialog = useCallback((row: OneRepMaxEntriesTableRow) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => setDeleteDialogOpen(false), []);

  const registerDeleteButton = useCallback((id: string, node: HTMLButtonElement | null) => {
    if (node) deleteButtonsRef.current.set(id, node);
    else deleteButtonsRef.current.delete(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRow) return;
    const index = rows.findIndex((row) => row.id === selectedRow.id);
    const neighborId = (rows[index + 1] ?? rows[index - 1])?.id ?? null;

    try {
      await deleteOneRepMax(selectedRow.id, {
        onSuccess: () => {
          focusAfterDeleteRef.current = { neighborId };
          closeDialog();
          successMessage({ message: t("info.one_rep_max_deleted") });
        },
      });
    } catch {
      errorMessage({ message: t("charting.one_rep_max_delete_dialog_error") });
    }
  }, [closeDialog, deleteOneRepMax, errorMessage, rows, selectedRow, successMessage, t]);

  // The dialog hands focus back to the deleted row's button, which is gone, so move it to the nearest entry.
  const handleDeleteDialogExited = useCallback(() => {
    if (!focusAfterDeleteRef.current) return;
    const { neighborId } = focusAfterDeleteRef.current;
    focusAfterDeleteRef.current = null;
    const neighborButton = neighborId ? deleteButtonsRef.current.get(neighborId) : undefined;
    (neighborButton ?? addButtonRef.current)?.focus();
  }, [addButtonRef]);

  const hasVariants = useMemo(() => rows.some((row) => row.variant), [rows]);

  const columns = useMemo<DataTableColumn<OneRepMaxEntriesTableRow>[]>(
    () => [
      { id: "date", header: t("global.date_label"), width: 180 },
      ...(hasVariants
        ? [
            {
              id: "variant",
              header: t("global.variant_label_text"),
              cell: (value: Variants | null) => (value ? t("global.variant_side", { context: value }) : "—"),
              width: 180,
            } as const,
          ]
        : []),
      {
        id: "value",
        header: t("global.one_rep_max_abbr_label"),
        width: 120,
        cell: (value) => <OneRepMaxEntryValue variant="TextS">{value}</OneRepMaxEntryValue>,
      },
      {
        id: "addedBy",
        header: t("charting.one_rep_max_table_added_by"),
        cell: (value) => {
          if (!value) return null;
          return (
            <OneRepMaxEntryAuthor variant="TextXS">
              {t("charting.one_rep_max_tooltip_added_by", { name: value })}
            </OneRepMaxEntryAuthor>
          );
        },
      },
      {
        id: null,
        header: t("global.actions_label"),
        align: "right",
        width: 48,
        cell: (_value: unknown, row: OneRepMaxEntriesTableRow) => (
          <OneRepMaxEntryDeleteButton row={row} onClick={openDeleteDialog} registerButton={registerDeleteButton} />
        ),
      },
    ],
    [hasVariants, openDeleteDialog, registerDeleteButton, t]
  );

  // Focus falls back to the add button when a deleted entry leaves no neighbour to focus.
  const memoHeaderSlotProps = useMemo(
    () => ({ addButton: { ref: addButtonRef, onClick: openAddOneRepMaxDialog, disabled: addDisabled } }),
    [addButtonRef, addDisabled, openAddOneRepMaxDialog]
  );

  const memoSlotProps = useMemo(
    () => ({
      // The parent panel scrolls; Chrome would otherwise swallow wheel events over the table.
      container: { elevation: 0, sx: { overscrollBehavior: "auto" } },
      bodyRow: {
        sx: {
          color: "grey.160",
          "&:hover, &.Mui-focusVisible": {
            backgroundColor: "grey.20",
          },
        },
      },
    }),
    []
  );

  if (!rows.length) return null;

  return (
    <>
      <OneRepMaxEntriesTableRoot>
        <OneRepMaxEntriesTableHeader slotProps={memoHeaderSlotProps} />
        <VirtualizedDataTable
          slotProps={memoSlotProps}
          data={rows}
          columns={columns}
          estimateSize={estimateSize}
          keyExtractor={keyExtractor}
          visuallyHiddenHeader
        />
      </OneRepMaxEntriesTableRoot>
      <DeleteOneRepMaxDialog
        open={deleteDialogOpen}
        entry={selectedRow?.summary}
        onCancel={closeDialog}
        onExited={handleDeleteDialogExited}
        onConfirm={handleDeleteConfirm}
        status={deleteStatus}
      />
    </>
  );
}

export default memo(OneRepMaxEntriesTable);
